import { type Session } from '@nhost/nhost-js/auth';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';

type AuthContextType = {
  user: Session['user'] | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const nhost = useNhostClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Subscribe to session changes
    const unsubscribe = nhost.sessionStorage.onChange(setSession);

    // Listen for storage events (cross-tab synchronization)
    const storageEventListener = (event: StorageEvent) => {
      if (event.key === 'nhostSession') {
        const newSession = event.newValue ? JSON.parse(event.newValue) : null;
        setSession(newSession);
      }
    };

    window.addEventListener('storage', storageEventListener);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', storageEventListener);
    };
  }, [nhost]);

  useEffect(() => {
    async function initializeSession() {
      setIsLoading(true);
      const currentSession = nhost.getUserSession();
      setSession(currentSession);
      setIsLoading(false);
    }
    initializeSession();
  }, [nhost]);

  const signOut = async () => {
    await nhost.auth.signOut({ refreshToken: session!.refreshToken });
    setSession(null);
  };

  // Update Sentry user whenever session changes
  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({
        id: session.user.id,
        email: session.user.email || undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [session?.user]);

  return (
    <AuthContext.Provider
      value={{
        user: session?.user || null,
        session,
        isAuthenticated: !!session,
        isLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
